/**
 * Agregação do recorte de tecnologia do Google Analytics.
 *
 * `technology_data`, em `GET /integrations/google-analytics/complete-data`, é a tabela
 * `google_analytics_technology_metrics` crua: **uma linha por (dia, navegador, sistema
 * operacional, categoria de dispositivo)**, em ordem de data. Fatiar essa lista com
 * `slice(0, 10)` não produz "os dez navegadores mais usados" — produz as dez primeiras
 * linhas do primeiro dia do período, com o mesmo navegador repetido uma vez por
 * dispositivo, e sem relação nenhuma com volume.
 *
 * A API passou a pedir `operatingSystem` ao GA4 (M-182 / D-041), o que multiplica as linhas
 * por navegador. Esta função é o que o card sempre quis dizer, e o que o deixa estável
 * quanto ao recorte que a API grava.
 */
export interface LinhaDeTecnologia {
  readonly browser?: string | null;
  readonly sessions?: number | null;
  readonly revenue?: number | null;
}

export interface NavegadorAgregado {
  readonly browser: string;
  readonly sessions: number;
  readonly revenue: number;
}

export const NAVEGADOR_DESCONHECIDO = "Navegador Desconhecido";

/**
 * Soma sessões e receita por navegador e devolve os `limite` de maior volume.
 *
 * O desempate é pelo nome do navegador: sem ele, dois navegadores com o mesmo número de
 * sessões — o que é a regra na cauda, onde tudo é zero — trocariam de lugar entre
 * renderizações, conforme a ordem em que a API devolvesse as linhas.
 */
export function agruparPorNavegador(
  linhas: readonly LinhaDeTecnologia[],
  limite = 10,
): NavegadorAgregado[] {
  const porNavegador = new Map<
    string,
    { browser: string; sessions: number; revenue: number }
  >();

  for (const linha of linhas) {
    const browser = linha.browser || NAVEGADOR_DESCONHECIDO;
    const atual = porNavegador.get(browser) ?? {
      browser,
      sessions: 0,
      revenue: 0,
    };
    atual.sessions += linha.sessions ?? 0;
    atual.revenue += linha.revenue ?? 0;
    porNavegador.set(browser, atual);
  }

  return [...porNavegador.values()]
    .sort(
      (a, b) => b.sessions - a.sessions || a.browser.localeCompare(b.browser),
    )
    .slice(0, limite);
}

/**
 * A largura da barra de um navegador, em porcentagem do líder.
 *
 * A escala anterior era fixa (`sessions / 100`) e saturava em 100% assim que um navegador
 * passava de dez mil sessões no período — com a agregação, isso deixou de ser um caso raro.
 */
export function proporcaoDoLider(
  navegador: NavegadorAgregado,
  lider: NavegadorAgregado | undefined,
): number {
  const teto = lider?.sessions ?? 0;
  return teto > 0 ? (navegador.sessions / teto) * 100 : 0;
}
